<script lang="ts" setup>
import { computed, onMounted, ref, Ref } from 'vue';
import Chip from 'primevue/chip';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useArchiveStore, useBillingAdminStore } from '@/store';
import { BillingAdminModal, BillingAdminTable } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const billingAdminStore = useBillingAdminStore();
const archiveStore = useArchiveStore();
const billingAdmins: Ref<BillingAdminTable[]> = ref([]);
const billingAdminCount: Ref<number> = computed(() => (billingAdmins.value ? billingAdmins.value.length : 0));
const billingAdminModalOpen: Ref<boolean> = ref(false);
const archivedVisible = ref(false);
const billingAdminItems: Ref<BillingAdminModal[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_ADMIN_INDEX])) getBillingAdmins(false);
});

const getBillingAdmins: (showArchived: boolean) => Promise<void> = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  await billingAdminStore
    .getBillingAdminsForTable(filterArchived)
    .then((result) => (billingAdmins.value = result))
    .catch((e) => {
      billingAdmins.value = [];
      throw e;
    });
};

const createMenuModel = (item: BillingAdminTable) => {
  const menuItems = [
    {
      label: 'Remove Billing Admin',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_BILLING_ADMINS]),
      command: () => {
        detachBillingAdmin([item.id], item.name);
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachBillingAdmins: (ids: number[]) => Promise<void> = async (ids) => {
  await billingAdminStore.attachBillingAdmins(ids);
  getBillingAdmins(false);
  billingAdminStore.doToast('Billing Admin(s) has been added', 'Added');
};

const detachBillingAdmin: (ids: number[], billingAdminName: string) => Promise<void> = async (
  ids,
  billingAdminName,
) => {
  await billingAdminStore.detachBillingAdmins(ids);
  getBillingAdmins(false);
  billingAdminStore.doToast(`${billingAdminName} has been removed from organization`, 'Removed');
};

const openAddBillingAdminModal = async () => {
  billingAdminModalOpen.value = true;
  billingAdminItems.value = await billingAdminStore.getBillingAdminsAvailableToAdd();
};

const onCancelAdd = () => (billingAdminModalOpen.value = false);

const onSaveAdd = (billingAdmins: BillingAdminModal[]) => {
  let billingAdminIds = billingAdmins.map((billingAdmins) => {
    return billingAdmins.value;
  });
  attachBillingAdmins(billingAdminIds);
  billingAdminModalOpen.value = false;
};
const showArchived = () => {
  archivedVisible.value = true;
  billingAdminStore.showArchived = true;
  getBillingAdmins(true);
};
const hideArchived = () => {
  archivedVisible.value = false;
  billingAdminStore.showArchived = false;
  getBillingAdmins(false);
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Billing Admins</h2>
      <Chip>{{ billingAdminCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATION_SCHOOL_SHOW_ARCHIVED])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_BILLING_ADMINS])"
          type="text"
          size="small"
          text="Add Billing Admin"
          icon-left="add"
          @click="openAddBillingAdminModal"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="billingAdmins"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="billingAdminStore.loading"
        :row-class="({ archived }) => (archived ? 'grey-row' : null)"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-billing-admin-table"
        state-storage="session"
        :class="{ 'table-loading': billingAdminStore.loading }"
      >
        <template v-if="billingAdminCount">
          <Column field="name" header="Billing Admin Name" header-style="color: #3B4242" sortable>
            <template #body="{ data }">
              <Icon
                v-if="data.archived"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_INDEX])"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.name" />
            </template>
          </Column>
          <Column
            body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </template>
        <template #empty>Billing Admins not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="billingAdminModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_BILLING_ADMINS])"
        title="Add Billing Admins"
        :items="billingAdminItems"
        :searchable="true"
        button-text="Add Billing Admins"
        placeholder="Search Billing Admins"
        person-type="Billing Admin"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
